import maplibregl, { type GeoJSONSource } from "maplibre-gl";

import { VEHICLE_CUSTOM_LAYER_ID } from "../../mapLayerIds";
import type { TransitPathLineStyle } from "./routeLine.types";
import { TRANSIT_PATH_LINE_STYLE_ACTIVE } from "./routeLineStyles.configuration";

/**
 * Builds [lng, lat] pairs from a shape or shape view for GeoJSON line layers.
 *
 * @param shape Compact shape or sliced view with `latAt` / `lonAt` accessors.
 * @returns Coordinates in MapLibre order.
 */
export function shapeViewToCoordinates( shape: {
    "length": number;
    "latAt":  ( i: number ) => number;
    "lonAt":  ( i: number ) => number;
} ): [number, number][] {
    const coordinates: [number, number][] = [];
    for ( let i = 0; i < shape.length; i += 1 ) {
        coordinates.push( [ shape.lonAt( i ), shape.latAt( i ) ] );
    }

    return coordinates;
}

/**
 * Builds a GeoJSON LineString Feature from an array of [lng, lat] coordinates.
 *
 * @param coordinates Array of [lng, lat] pairs.
 * @returns GeoJSON Feature with LineString geometry.
 */
export function buildLineGeoJSON( coordinates: [number, number][] ): GeoJSON.Feature<GeoJSON.LineString> {
    return {
        "type":       "Feature",
        "properties": {},
        "geometry":   {
            "type": "LineString",
            coordinates,
        },
    };
}

/**
 * Safely removes a layer and its associated source from the map.
 *
 * @param map MapLibre map instance.
 * @param layerId Layer identifier.
 * @param sourceId Source identifier.
 */
export function removeLayerAndSource( map: maplibregl.Map, layerId: string, sourceId: string ): void {
    if ( map.getLayer( layerId ) != null ) {
        map.removeLayer( layerId );
    }
    if ( map.getSource( sourceId ) != null ) {
        map.removeSource( sourceId );
    }
}

/**
 * Creates a GeoJSON source + line layer on the map if they don't already exist.
 *
 * @param map MapLibre map instance.
 * @param sourceId Source identifier.
 * @param layerId Layer identifier.
 * @param coordinates Line coordinates as [lng, lat] pairs.
 * @param colorHex CSS hex color for the line.
 * @param style Line paint style (opacity, width, optional dash).
 * @param beforeLayerId Optional layer id to insert below; falls back to vehicle custom layer.
 */
export function ensureGeoJsonLineLayer(
    map: maplibregl.Map,
    sourceId: string,
    layerId: string,
    coordinates: [number, number][],
    colorHex: string,
    style: TransitPathLineStyle = TRANSIT_PATH_LINE_STYLE_ACTIVE,
    beforeLayerId?: string,
): void {
    if ( coordinates.length < 2 ) {
        return;
    }

    map.addSource( sourceId, {
        "type": "geojson",
        "data": buildLineGeoJSON( coordinates ),
    } );

    const beforeId = beforeLayerId !== undefined && map.getLayer( beforeLayerId ) != null
        ? beforeLayerId
        : map.getLayer( VEHICLE_CUSTOM_LAYER_ID ) != null ? VEHICLE_CUSTOM_LAYER_ID : undefined;
    const paint: maplibregl.LineLayerSpecification["paint"] = {
        "line-color":   colorHex,
        "line-width":   style.lineWidth,
        "line-opacity": style.lineOpacity,
    };
    if ( style.lineDasharray !== undefined ) {
        paint["line-dasharray"] = style.lineDasharray;
    }

    map.addLayer( {
        "id":     layerId,
        "type":   "line",
        "source": sourceId,
        paint,
    }, beforeId );
}

/**
 * Updates an existing GeoJSON line source, or creates the layer when missing.
 *
 * @param map MapLibre map instance.
 * @param sourceId Source identifier.
 * @param layerId Layer identifier.
 * @param coordinates Line coordinates as [lng, lat] pairs.
 * @param colorHex CSS hex color for the line.
 * @param style Line paint style.
 * @param beforeLayerId Optional layer id to insert below when creating.
 * @returns Whether data was applied (source existed or layer was created).
 */
export function updateGeoJsonLineData(
    map: maplibregl.Map,
    sourceId: string,
    layerId: string,
    coordinates: [number, number][],
    colorHex: string,
    style: TransitPathLineStyle = TRANSIT_PATH_LINE_STYLE_ACTIVE,
    beforeLayerId?: string,
): boolean {
    if ( coordinates.length < 2 ) {
        return false;
    }

    const existingSource = map.getSource( sourceId );
    if ( existingSource !== undefined && "setData" in existingSource ) {
        ( existingSource as GeoJSONSource ).setData( buildLineGeoJSON( coordinates ) );

        return true;
    }

    ensureGeoJsonLineLayer( map, sourceId, layerId, coordinates, colorHex, style, beforeLayerId );

    return true;
}

/**
 * Adds an itinerary leg line layer below the vehicle custom layer when present.
 *
 * @param map Active MapLibre map.
 * @param sourceId GeoJSON source id.
 * @param layerId Line layer id.
 * @param geojsonLine Feature geometry for the leg.
 * @param colorHex Leg color.
 * @param lineWidth Initial line width.
 * @param lineOpacity Initial line opacity.
 */
export function ensureItineraryLegLineLayer(
    map: maplibregl.Map,
    sourceId: string,
    layerId: string,
    geojsonLine: GeoJSON.Feature,
    colorHex: string,
    lineWidth: number,
    lineOpacity: number,
): void {
    map.addSource( sourceId, {
        "type": "geojson",
        "data": geojsonLine,
    } );

    const beforeId = map.getLayer( VEHICLE_CUSTOM_LAYER_ID ) != null ? VEHICLE_CUSTOM_LAYER_ID : undefined;
    map.addLayer( {
        "id":     layerId,
        "type":   "line",
        "source": sourceId,
        "paint":  {
            "line-color":   colorHex,
            "line-width":   lineWidth,
            "line-opacity": lineOpacity,
        },
    }, beforeId );
}
